// Shared eligibility heuristic for "is this calendar event worth a
// pre-meeting brief?" Used by BOTH the brief agent
// (apps/workers/src/agents/meeting/brief.ts) and the /meetings UI
// (apps/web/src/pages/Meetings.tsx) so the two surfaces never drift.
// See ADR-0013 §"Eligibility filter" for the rationale.
//
// Rejects:
//   - solo focus blocks / personal reminders (< 2 attendees)
//   - all-day events (travel, vacation, etc.)
//   - cancelled / tentative events
//   - internal-only events without a meeting_url (likely co-located
//     deep work)
//
// Accepts:
//   - video calls (meeting_url present)
//   - 1:1s and team meetings with an external participant
//
// The first three rejection criteria are pushed to the SQL WHERE
// clause (and indexed in migration 0021 for the agent side); this
// module covers ONLY the attendee-shape checks which run cleanly in
// JS on both surfaces.

// Shape of an attendee inside calendar_events.attendees JSONB, as
// written by apps/workers/src/connectors/calendar-mapper.ts.
export type CalendarEventAttendee = {
  email: string | null;
  name: string | null;
  response_status: string | null;
  organizer: boolean;
  optional: boolean;
  self: boolean;
};

// Lowercased domain after the @ sign, or '' on malformed input.
// Exported so callers can compute it once per render and reuse.
export function ownerDomainOf(email: string | null | undefined): string {
  if (!email) return '';
  const at = email.indexOf('@');
  return at >= 0 ? email.slice(at + 1).toLowerCase() : '';
}

// `is_external` semantics: attendees with a different email domain
// than the owner's. `self` short-circuits to false because the calendar
// owner is never "external" to themselves regardless of domain.
//
// Subdomain attendees (e.g. sales@us.example.com vs joel@example.com)
// are flagged external — this is an exact-tail-match comparison.
// Acceptable for our flat-domain setup; revisit if Reverra adopts
// multi-subdomain SSO.
export function isExternalAttendee(att: CalendarEventAttendee, ownerDomain: string): boolean {
  if (att.self) return false;
  const email = att.email?.toLowerCase() ?? '';
  if (!email || !ownerDomain) return false;
  const atIdx = email.lastIndexOf('@');
  if (atIdx < 0) return false;
  return email.slice(atIdx + 1) !== ownerDomain;
}

// At least 2 attendees → skips solo focus blocks / personal reminders
// that would otherwise burn tokens for no useful synthesis.
//
// Note: Google's API populates `attendees` with the owner only when
// they're a participant (which they almost always are — Calendar lists
// you on events you've accepted or scheduled). For an event Joel
// watches on a shared resource calendar where he's NOT listed as an
// attendee, attendees.length could be 1 (just the organizer). That
// edge case is correctly skipped.
export const MIN_ATTENDEES_FOR_BRIEF = 2 as const;

// The full eligibility predicate. Pure: same input → same output, no
// I/O. Both surfaces call this with the SAME attendee shape and the
// SAME ownerDomain string, so they cannot disagree by construction.
//
// Defensive against a non-array attendees value (returns false) — the
// DB column is NOT NULL with default `[]`, but a tampered or legacy
// row could theoretically have a non-array shape.
export function isCalendarEventBriefEligible(
  row: { attendees: CalendarEventAttendee[] | null | undefined; meeting_url: string | null },
  ownerDomain: string,
): boolean {
  if (!Array.isArray(row.attendees)) return false;
  if (row.attendees.length < MIN_ATTENDEES_FOR_BRIEF) return false;
  if (row.meeting_url) return true;
  return row.attendees.some((a) => isExternalAttendee(a, ownerDomain));
}
